import React from "react";
import { ChakraProvider, extendTheme, useColorMode } from "@chakra-ui/react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ImagePreloader } from "./components/ImagePreloader";
import { registerServiceWorker } from "./utils/registerSW";

// Pages
import GreshamCapitalPartners from './pages/GreshamCapitalPartners';
import About from './pages/About';
import Fund from './pages/Fund';
import Advisory from './pages/Advisory';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CheckoutSuccess from './pages/CheckoutSuccess';
import CheckoutCancel from './pages/CheckoutCancel';
import Disclaimer from './pages/Disclaimer';

// Member / admin portal (single folder)
import LoginPage from './portal/LoginPage';
import RegisterPage from './portal/RegisterPage';
import ForgotPasswordPage from './portal/ForgotPasswordPage';
import ResetPasswordPage from './portal/ResetPasswordPage';
import MemberDashboard from './portal/MemberDashboard';
import ArticlesPage from './portal/ArticlesPage';
import ArticleViewPage from './portal/ArticleViewPage';
import ProtectedRoute from './portal/ProtectedRoute';
import AdminRoute from './portal/AdminRoute';
import AdminDashboard from './portal/AdminDashboard';
import AdminPlans from './portal/AdminPlans';
import AdminSubscribers from './portal/AdminSubscribers';
import AdminArticles from './portal/AdminArticles';
import AdminCreateArticle from './portal/AdminCreateArticle';
import AdminArticleDetailPage from './portal/AdminArticleDetail';
import AdminEditArticlePage from './portal/AdminArticleEdit';

// Force light mode theme configuration
const customTheme = extendTheme({
  config: {
    initialColorMode: "light",
    useSystemColorMode: false,
  },
});

// Component to force light mode on mount and keep it locked
const ForceLightMode: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { colorMode, setColorMode } = useColorMode();

  React.useEffect(() => {
    if (colorMode !== "light") {
      setColorMode("light");
    }
    localStorage.setItem("chakra-ui-color-mode", "light");
  }, [colorMode, setColorMode]);

  return <>{children}</>;
};

const AppContent = () => {
  return (
    <Routes>
      <Route path="/" element={<GreshamCapitalPartners />} />
      <Route path="/about" element={<About />} />
      <Route path="/fund" element={<Fund />} />
      <Route path="/advisory" element={<Advisory />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      <Route path="/payment-success" element={<CheckoutSuccess />} />
      <Route path="/payment-cancel" element={<CheckoutCancel />} />

      {/* Member auth & content */}
      <Route path="/member/login" element={<LoginPage />} />
      <Route path="/member/register" element={<RegisterPage />} />
      <Route path="/member/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/member/reset-password" element={<ResetPasswordPage />} />
      <Route element={<ProtectedRoute />}>
        <Route path="/member/dashboard" element={<Navigate to="/member/articles" replace />} />
        <Route path="/member/subscription" element={<MemberDashboard />} />
        <Route path="/member/articles" element={<ArticlesPage />} />
        <Route path="/member/articles/:id" element={<ArticleViewPage />} />
      </Route>

      {/* Admin area */}
      <Route element={<AdminRoute />}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/plans" element={<AdminPlans />} />
        <Route path="/admin/subscribers" element={<AdminSubscribers />} />
        <Route path="/admin/articles" element={<AdminArticles />} />
        <Route path="/admin/articles/new" element={<AdminCreateArticle />} />
        <Route path="/admin/articles/:id" element={<AdminArticleDetailPage />} />
        <Route path="/admin/articles/:id/edit" element={<AdminEditArticlePage />} />
      </Route>
    </Routes>
  );
};

export const App = () => {
  React.useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <Router>
      <ChakraProvider theme={customTheme}>
        <ForceLightMode>
          <ImagePreloader />
          <AppContent />
        </ForceLightMode>
      </ChakraProvider>
    </Router>
  );
};
